import SlidingToggle from "@client/utils/SlidingToggle";
import type { Disclosure as DisclosureType } from "@engine/round/aggregate";
import clsx from "clsx";
import { useEffect, useId, useState } from "react";

export default function DisclosureInfo({
  proposalId,
  memberId,
  disclosure,
  handleDisclosureChange,
}: {
  proposalId: string;
  memberId: string;
  disclosure: DisclosureType | undefined;
  handleDisclosureChange: (
    proposalId: string,
    memberId: string,
    disclosureInfo: {
      selfRecused: boolean;
      disclosure: string;
      hasDisclosure: boolean;
    },
  ) => Promise<void>;
}) {
  const id = useId();
  const disclosureTextProp = disclosure != null ? disclosure.disclosure : "";

  const [text, setText] = useState(disclosureTextProp);

  useEffect(() => {
    setText(disclosureTextProp);
  }, [disclosureTextProp]);

  const handleToggleHasDisclosure = (isActive: boolean) => {
    if (!disclosure) {
      // First time setting up a disclosure
      void handleDisclosureChange(proposalId, memberId, {
        selfRecused: false,
        disclosure: text,
        hasDisclosure: isActive,
      });
    } else {
      // Just toggle the hasDisclosure flag
      void handleDisclosureChange(proposalId, memberId, {
        selfRecused: disclosure.selfRecused,
        disclosure: text,
        hasDisclosure: isActive,
      });
    }
  };

  const disclosureRadios = (
    <div className="flex items-center gap-1">
      <label className="text-sm">I have a disclosure:</label>
      <fieldset className="flex items-center gap-1">
        <div className="flex items-center gap-0.5">
          <input
            type="radio"
            id={`${id}-yes`}
            name={`${id}-disclosure`}
            value="yes"
            checked={disclosure?.hasDisclosure === true}
            onChange={() => handleToggleHasDisclosure(true)}
          />
          <label htmlFor={`${id}-yes`}>Yes</label>
        </div>
        <div className="flex items-center gap-0.5">
          <input
            type="radio"
            id={`${id}-no`}
            name={`${id}-disclosure`}
            value="no"
            checked={disclosure?.hasDisclosure === false}
            onChange={() => handleToggleHasDisclosure(false)}
          />
          <label htmlFor={`${id}-no`}>No</label>
        </div>
      </fieldset>
    </div>
  );

  if (!disclosure || !disclosure.hasDisclosure) {
    return (
      <>
        <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
          {disclosureRadios}
          <textarea
            className={clsx(
              "flex-1 min-w-96 border border-transparent rounded-md p-1",
              "max-h-0 overflow-hidden opacity-0 cursor-default",
            )}
          />
        </td>
        <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
          <div className="opacity-0 max-h-0 overflow-hidden">
            <SlidingToggle
              label="Self-recuse"
              state={false}
              onChange={() => {}}
              disabled={true}
            />
          </div>
        </td>
      </>
    );
  }

  // Disclosure is active
  return (
    <>
      <td className="py-2 px-4 border-b light:border-gray-200 dark:border-stone-700">
        {disclosureRadios}
        <textarea
          className={clsx(
            "flex-1 min-w-96 border rounded-md p-1",
            "border light:border-stone-700 dark:bg-stone-900",
            "outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 light:focus:outline-blue-600 dark:focus:outline-blue-400",
          )}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() =>
            void handleDisclosureChange(proposalId, memberId, {
              selfRecused: disclosure.selfRecused,
              disclosure: text,
              hasDisclosure: disclosure.hasDisclosure,
            })
          }
        />
      </td>
      <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
        <SlidingToggle
          label="Self-recuse"
          state={disclosure.selfRecused}
          onChange={(value) => {
            void handleDisclosureChange(proposalId, memberId, {
              selfRecused: value,
              disclosure: text,
              hasDisclosure: disclosure.hasDisclosure,
            });
          }}
        />
      </td>
    </>
  );
}
